
import { TSData, TSWeeklyData, RegStatData, MapData, HMData, ForecastData, HMTSData, ProfileData, FairmodeData, RadarData, ContourData, ScatData } from '../../types/data';
import { selectFirstModel, reorderDataByCfgOrder } from '../../utils'

import { Aeroval, Cfg, Experiments } from '../../types/global';
declare var aeroval: Aeroval;

export async function getExperimentsData(project: string): Promise<Experiments | undefined> {
    const url = `${window.API_ROOT}/experiments/${project}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}

export async function getCfgData(project: string, experimentName: string): Promise<Cfg | undefined> {
    const url = `${window.API_ROOT}/cfg/${project}/${experimentName}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}

export async function getMenu(): Promise<Aeroval['Menu'] | undefined> {
    const url = `${window.API_ROOT}/menu/${aeroval.project}/${aeroval.experiment.name}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache'
    });
    if (response.ok) {
        return response.json()
    } else {
        return undefined
    }
}

export async function getTSData(project: string, experimentName: string, stationName: string, observation: string, parameterDir: string, layer: string): Promise<TSData | undefined> {
    const url = `${window.API_ROOT}/ts/${project}/${experimentName}/${stationName}/${observation}/${parameterDir}/${layer}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}

export async function getTSWeeklyData(project: string, experimentName: string, stationName: string, observation: string, parameterDir: string, layer: string): Promise<TSWeeklyData | undefined> {
    const url = `${window.API_ROOT}/ts_weekly/${project}/${experimentName}/${stationName}/${observation}/${parameterDir}/${layer}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}


export async function getRegionalStatisticsData(project: string, experimentName: string, parameterDir: string, observation: string, layer: string, frequency: string): Promise<RegStatData | undefined> {
    const url = `${window.API_ROOT}/regional_statistics/${project}/${experimentName}/${frequency}/${parameterDir}/${observation}/${layer}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}

export async function getMapData(project: string, experimentName: string, observation: string, parameterDir: string, layer: string, modelDir: string, modelVar: string, time: string, season: string, frequency: string): Promise<MapData | undefined> {
    if (!modelDir || modelDir === 'dummy') {
        const firstModel = selectFirstModel(parameterDir, observation);
        modelDir = firstModel.dir
        modelVar = firstModel.var
    }
    const keyTime = `${time}-${season}`.replace('-All', '-all')
    const url = `${window.API_ROOT}/map/${project}/${experimentName}/${observation}/${parameterDir}/${layer}/${modelDir}/${modelVar}/${time.replaceAll('/', '')}/${frequency}/${keyTime}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data as MapData;
    }
}

export async function getMapDataAllModels(project: string, experimentName: string, observation: string, parameterDir: string, layer: string, models: string[], time: string, season: string, frequency: string): Promise<any> {
    const keyTime = `${time}-${season}`.replace('-All', '-all')
    const models_url = []
    for (const model of models) {
        // skip the model if it has been clicked away
        if (aeroval.seriesVisibility[model] === false) continue;
        const modelVar = aeroval.menu[parameterDir].obs[observation][layer][model].model_var
        const model_url = `${window.API_ROOT}/map/${project}/${experimentName}/${observation}/${parameterDir}/${layer}/${model}/${modelVar}/${time.replaceAll('/', '')}/${frequency}/${keyTime}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
        models_url.push(model_url)
    }

    try {
        const modelsResponse = await Promise.all(
            models_url.map(async url => {
                const res = await fetch(url);
                if (res.ok) {
                    return res.json();
                } else {
                    console.warn(`Failed to fetch ${url}: ${res.status} ${res.statusText}`);
                }
            })
        );
        return modelsResponse; // Return the fetched data
    } catch (error) {
        console.error('Error loading configurations:', error);
        throw error; // Rethrow to allow the caller to handle it
    }
}

export async function getHeatmapData(project: string, experimentName: string, frequency: string, region: string, time: string, season: string): Promise<HMData | undefined> {
    const keyTime = `${time}-${season}`.replace('-All', '-all')
    const url = `${window.API_ROOT}/heatmap/${project}/${experimentName}/${frequency}/${region}/${keyTime}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        const orderedData = aeroval.cfg.webdisp_opts.var_order_menu?.length > 0 ? reorderDataByCfgOrder(data) : data
        return orderedData;
    }
}

export async function getForecastData(project: string, experimentName: string, region: string, observation: string, parameterDir: string, layer: string): Promise<ForecastData | undefined> {
    const url = `${window.API_ROOT}/forecast/${project}/${experimentName}/${region}/${observation}/${parameterDir}/${layer}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}

export async function getStatisticsTimeSeriesData(project: string, experimentName: string, region: string, observation: string, parameterDir: string, layer: string): Promise<HMTSData | undefined> {
    const url = `${window.API_ROOT}/hm_ts/${project}/${experimentName}/${region}/${observation}/${parameterDir}/${layer}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const hmTS = await response.json()
        const orderedData: any = {};
        for (const menuItem in hmTS) {
            orderedData[menuItem] = hmTS[menuItem];
        }
        return orderedData;
    }
}

export async function getProfileData(project: string, experimentName: string, stationName: string, observation: string, parameterDir: string): Promise<ProfileData | undefined> {
    const url = `${window.API_ROOT}/profiles/${project}/${experimentName}/${stationName}/${observation}/${parameterDir}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}

export async function getFairmodeData(project: string, experimentName: string, region: string, observation: string, parameterDir: string, layer: string, modelDir: string, time: string): Promise<FairmodeData | undefined> {
    const url = `${window.API_ROOT}/fairmode/${project}/${experimentName}/${region}/${observation}/${parameterDir}/${layer}/${modelDir}/${time.replaceAll('/', '')}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    //const url = `${window.API_ROOT}/file/${project}/${experimentName}/fairmode/${region}_${observation}_${parameterDir}_${layer}_${modelDir}_${time}.json${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}

export async function getRadarData(project: string, experimentName: string, region: string, observation: string, parameterDir: string, layer: string, modelDir: string, time: string): Promise<RadarData | undefined> {
    const url = `${window.API_ROOT}/radar/${project}/${experimentName}/${region}/${observation}/${parameterDir}/${layer}/${modelDir}/${time.replaceAll('/', '')}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache',
        method: 'GET',
    });
    if (response.ok) {
        const data = await response.json()
        return data
    }
}



type StripesData = {
    [year: number]: {
        [season in 'DJF' | 'MAM' | 'JJA' | 'SON']: {
            [statistics: string]: number | null
        }
    }
}
export async function getStripesData(project: string, experimentName: string, frequency: string, region: string, parameterDir: string, observation: string, layer: string, modelDir: string, modelVar: string): Promise<StripesData | undefined> {
    var stripesData = {} as any
    const periods = aeroval.cfg.time_cfg.periods
    const seasons = aeroval.cfg.time_cfg.SEASONS
    for (let period of periods) {
        if (period.includes('-')) continue
        stripesData[period] = {}
        for (let season of seasons) {
            if (season == 'all') continue
            let hmData = await getHeatmapData(project, experimentName, frequency, region, period, season)
            if (hmData) stripesData[period][season] = hmData[parameterDir][observation][layer][modelDir][modelVar][region][`${period}-${season}`]
        }
    }
    return stripesData
}

export async function getContour(project: string, experimentName: string, variable: string, model: string, time: number): Promise<ContourData | undefined> {
    const url = `${window.API_ROOT}/contour/${project}/${experimentName}/${variable}/${model}?time=${time}${window.DATA_PATH && `&data_path=${window.DATA_PATH}`}`;
    try {
        const response = await fetch(url, {
            cache: 'no-cache'
        });
        return response.ok ? response.json() : undefined;
    } catch (error) {
        console.warn(`contour file: ${error}`);
        return undefined;
    }
}

export async function getOverlay(project: string, experimentName: string, variable: string, model: string, time: number): Promise<string | undefined> {
    const url = `${window.API_ROOT}/maps_overlays/${project}/${experimentName}/${model}/${variable}/${time}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    try {
        const response = await fetch(url, {
            cache: 'no-cache'
        });
        return response.ok ? response.url : undefined;
    } catch (error) {
        console.warn(`overlay file: ${error}`);
        return undefined;
    }
}

export async function getScatterPlotData(project: string, experimentName: string, observation: string, obsVariable: string, layer: string, model: string, modVariable: string, time: string): Promise<ScatData | undefined> {
    const url = `${window.API_ROOT}/scat/${project}/${experimentName}/${observation}/${obsVariable}/${layer}/${model}/${modVariable}/${time.replaceAll('/', '')}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    try {
        const response = await fetch(url, {
            cache: 'no-cache'
        });
        return response.ok ? await response.json() : undefined;
    } catch (error) {
        console.warn(`overlay file: ${error}`);
        return undefined;
    }
}

export async function getConfigModificationTime(project: string, experimentName: string): Promise<any> {
    const url = `${window.API_ROOT}/mtime_config/${project}/${experimentName}${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache'
    });
    if (response.ok) {
        return response.json()
    } else {
        return null
    }
}

export async function getModelsStyle(project: string): Promise<any> {
    const url = `${window.API_ROOT}/models_style/${project}/${window.DATA_PATH && `?data_path=${window.DATA_PATH}`}`;
    const response = await fetch(url, {
        cache: 'no-cache'
    });
    if (response.ok) {
        return response.json()
    } else {
        return null
    }
}

